import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Bell, BellOff, X, Check, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Convert base64 to Uint8Array for push subscription
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const PushNotificationPrompt = () => {
  const { user } = useAuth();
  const [permission, setPermission] = useState('default');
  const [showPrompt, setShowPrompt] = useState(false);
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    // Check if push notifications are supported
    if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
      return;
    }

    // Get current permission state
    setPermission(Notification.permission);

    // Check if already subscribed
    checkSubscription();

    // Show prompt after 3 seconds if not decided yet
    if (Notification.permission === 'default' && user) {
      const timer = setTimeout(() => {
        // Check if user has dismissed before
        const dismissed = localStorage.getItem('pushNotificationDismissed');
        const installBannerVisible = document.querySelector('[data-testid="pwa-install-banner"]');
        if (!dismissed && !installBannerVisible) {
          setShowPrompt(true);
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  const checkSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setSubscribed(!!subscription);
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const subscribeToPush = async () => {
    setLoading(true);
    try {
      // Request notification permission
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result !== 'granted') {
        setShowPrompt(false);
        return;
      }

      // Get VAPID public key from server
      const { data: { publicKey } } = await axios.get(`${API_URL}/api/push/vapid-key`);

      // Subscribe to push notifications
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });

      // Send subscription to server
      const subscriptionJson = subscription.toJSON();
      await axios.post(`${API_URL}/api/push/subscribe`, {
        endpoint: subscriptionJson.endpoint,
        p256dh: subscriptionJson.keys.p256dh,
        auth: subscriptionJson.keys.auth
      });

      setSubscribed(true);
      setShowPrompt(false);

      // Show success notification
      new Notification('Notifikasi Aktif! 🔔', {
        body: 'Anda akan menerima notifikasi penting dari Kos Cemara',
        icon: '/icons/icon-192x192.png'
      });

    } catch (error) {
      console.error('Error subscribing to push:', error);
    } finally {
      setLoading(false);
    }
  };

  const dismissPrompt = () => {
    setShowPrompt(false);
    localStorage.setItem('pushNotificationDismissed', 'true');
  };

  // Don't show if not supported or already decided
  if (!('Notification' in window) || !user || permission === 'denied') {
    return null;
  }

  // Show floating prompt
  if (showPrompt && !subscribed) {
    return (
      <div className="fixed bottom-20 md:bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 z-50 animate-slide-up">
        <div className="bg-card border shadow-lg rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Bell className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-sm">Aktifkan Notifikasi</h4>
              <p className="text-xs text-muted-foreground mt-1">
                Dapatkan pemberitahuan tagihan baru dan reminder pembayaran langsung di HP Anda
              </p>
              <div className="flex gap-2 mt-3">
                <Button
                  size="sm"
                  onClick={subscribeToPush}
                  disabled={loading}
                  className="flex-1"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-1" />
                      Aktifkan
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={dismissPrompt}
                  disabled={loading}
                >
                  Nanti
                </Button>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 -mt-1 -mr-1"
              onClick={dismissPrompt}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// Settings component for managing push notifications
export const PushNotificationSettings = () => {
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);

  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
      checkSubscription();
    }
  }, []);

  const checkSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setSubscribed(!!subscription);
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const handleToggle = async () => {
    setLoading(true);
    try {
      if (subscribed) {
        // Unsubscribe
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
        }
        await axios.delete(`${API_URL}/api/push/unsubscribe`);
        setSubscribed(false);
      } else {
        // Subscribe
        const result = await Notification.requestPermission();
        setPermission(result);

        if (result === 'granted') {
          const { data: { publicKey } } = await axios.get(`${API_URL}/api/push/vapid-key`);
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicKey)
          });

          const subscriptionJson = subscription.toJSON();
          await axios.post(`${API_URL}/api/push/subscribe`, {
            endpoint: subscriptionJson.endpoint,
            p256dh: subscriptionJson.keys.p256dh,
            auth: subscriptionJson.keys.auth
          });

          setSubscribed(true);
        }
      }
    } catch (error) {
      console.error('Error toggling subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendTestNotification = async () => {
    setTestLoading(true);
    try {
      await axios.post(`${API_URL}/api/push/test`);
    } catch (error) {
      console.error('Error sending test notification:', error);
    } finally {
      setTestLoading(false);
    }
  };

  if (!('Notification' in window) || !('PushManager' in window)) {
    return (
      <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
        <BellOff className="w-5 h-5 text-muted-foreground" />
        <div>
          <p className="text-sm font-medium">Push Notification tidak didukung</p>
          <p className="text-xs text-muted-foreground">Browser Anda tidak mendukung push notification</p>
        </div>
      </div>
    );
  }

  if (permission === 'denied') {
    return (
      <div className="flex items-center gap-3 p-4 bg-destructive/10 rounded-lg">
        <BellOff className="w-5 h-5 text-destructive" />
        <div>
          <p className="text-sm font-medium">Notifikasi Diblokir</p>
          <p className="text-xs text-muted-foreground">Aktifkan notifikasi di pengaturan browser Anda</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between p-4 bg-card border rounded-lg">
        <div className="flex items-center gap-3">
          {subscribed ? (
            <Bell className="w-5 h-5 text-primary" />
          ) : (
            <BellOff className="w-5 h-5 text-muted-foreground" />
          )}
          <div>
            <p className="text-sm font-medium">Push Notification</p>
            <p className="text-xs text-muted-foreground">
              {subscribed ? 'Notifikasi aktif' : 'Notifikasi nonaktif'}
            </p>
          </div>
        </div>
        <Button
          variant={subscribed ? 'outline' : 'default'}
          size="sm"
          onClick={handleToggle}
          disabled={loading}
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : subscribed ? (
            'Nonaktifkan'
          ) : (
            'Aktifkan'
          )}
        </Button>
      </div>

      {subscribed && (
        <Button
          variant="outline"
          size="sm"
          onClick={sendTestNotification}
          disabled={testLoading}
          className="w-full"
        >
          {testLoading ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Bell className="w-4 h-4 mr-2" />
          )}
          Kirim Test Notifikasi
        </Button>
      )}
    </div>
  );
};

export default PushNotificationPrompt;
